/**
 * Product media gallery: a swipeable scroll-snap carousel synced to
 * thumbnails (desktop) / dot pagination (mobile), a fullscreen lightbox,
 * and variant-aware media switching.
 *
 * DOM contract: `[data-gallery-track]` holds one
 * `.product-main__media-item[data-media-id]` per product media item (in
 * source order). Optional `[data-gallery-prev]`/`[data-gallery-next]`
 * arrows, `[data-gallery-thumb]`/`[data-gallery-dot]` buttons (one per
 * item, `data-media-index` matching track order), a `[data-gallery-zoom]`
 * button inside image-type items, and a `<dialog data-gallery-lightbox>`
 * with its own `[data-gallery-lightbox-track]` and nav/close buttons.
 * `data-initial-media-id` seeds the item shown on load (used to honor the
 * selected variant's featured image without a layout shift on connect).
 *
 * Touch/desktop swipe comes from native CSS scroll-snap on the track, not
 * custom drag handling. The lightbox reuses `<dialog>` for focus trapping,
 * Escape-to-close, and the scrim, so it moves (not clones) the actual media
 * nodes into `[data-gallery-lightbox-track]` on open and back on close —
 * video/model-viewer elements are never duplicated or reloaded.
 *
 * Events listened for: `vellum:variant-media` (dispatched by
 * assets/product-form.js on variant change) with `detail.mediaId` — jumps
 * the gallery to the matching media item.
 */
class VellumProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.items = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    this.lightboxTrack = this.querySelector('[data-gallery-lightbox-track]');
    this.activeIndex = 0;
    this.isLightboxOpen = false;

    if (this.items.length > 1) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.track,
        threshold: 0.6,
      });
      this.items.forEach((item) => this.observer.observe(item));
    }

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.mediaIndex)));
    });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.mediaIndex)));
    });
    this.prevButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));

    this.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.closest('[data-media-index]').dataset.mediaIndex);
        this.openLightbox(index);
      });
    });

    if (this.lightbox) {
      this.querySelector('[data-gallery-lightbox-prev]')?.addEventListener('click', () => this.goTo(this.activeIndex - 1, true));
      this.querySelector('[data-gallery-lightbox-next]')?.addEventListener('click', () => this.goTo(this.activeIndex + 1, true));
      this.querySelector('[data-gallery-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });
      this.lightbox.addEventListener('close', this.onLightboxClose.bind(this));
      this.lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1, true);
        if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1, true);
      });
    }

    window.addEventListener('vellum:variant-media', this.onVariantMedia.bind(this));
    window.addEventListener('resize', () => {
      if (!this.isLightboxOpen) this.goTo(this.activeIndex, false, 'auto');
    });

    const initialMediaId = this.dataset.initialMediaId;
    const initialIndex = initialMediaId ? this.items.findIndex((item) => item.dataset.mediaId === initialMediaId) : 0;
    this.goTo(initialIndex > -1 ? initialIndex : 0, false, 'auto');
  }

  onIntersect(entries) {
    if (this.isLightboxOpen) return;
    const visible = entries.find((entry) => entry.isIntersecting);
    if (visible) this.updateActive(this.items.indexOf(visible.target));
  }

  goTo(index, inLightbox = false, behavior = 'smooth') {
    const clamped = Math.max(0, Math.min(index, this.items.length - 1));
    const track = inLightbox ? this.lightboxTrack : this.track;
    // Each item is exactly one track-width wide, so index * clientWidth is
    // the exact scroll offset. scrollIntoView is avoided here because it can
    // also scroll ancestor containers (the page itself) vertically into
    // view, which is wrong for a horizontal-only carousel.
    if (track) track.scrollTo({ left: clamped * track.clientWidth, behavior });
    this.updateActive(clamped);
  }

  updateActive(index) {
    this.activeIndex = index;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.items.length - 1;
  }

  onVariantMedia(event) {
    const mediaId = event.detail?.mediaId;
    if (!mediaId) return;
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index > -1) this.goTo(index);
  }

  openLightbox(index) {
    if (!this.lightbox) return;
    this.isLightboxOpen = true;
    this.items.forEach((item) => this.lightboxTrack.appendChild(item));
    this.lightbox.showModal();
    this.goTo(index, true, 'auto');
  }

  onLightboxClose() {
    this.isLightboxOpen = false;
    this.items.forEach((item) => this.track.appendChild(item));
    this.goTo(this.activeIndex, false, 'auto');
    this.items[this.activeIndex]?.querySelector('[data-gallery-zoom]')?.focus();
  }
}

if (!customElements.get('vellum-product-gallery')) {
  customElements.define('vellum-product-gallery', VellumProductGallery);
}
